import { useAuthContext } from 'contexts/auth.context';
import { checkSubUserPermissions } from 'helpers/user.helpers';
import { GetFilteredSimsParams } from 'lib/api/requests/sim/sim.requests.types';
import { QueryData } from 'lib/api/utils';
import { useFilteredSims } from 'lib/hooks/sim/sim.queries';
import React, { PropsWithChildren, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TODO } from 'types/lib';
import { UserRole } from 'types/shared';
import { SprSimListSortKey, SprSimListTabName } from 'types/your-sims';
import { TABS_PATH_NAME_MAP } from './config';
import { ModalsProvider } from './_components/ModalsProvider/ModalsProvider';

type SimRecord = QueryData<typeof useFilteredSims>['records'][number];

type YourSimsContextProviderProps = {
  oldSelectedSims: TODO[];
  setOldSelectedSims: (sims: TODO[]) => void;
  oldSimsState: TODO[];
  oldSimDataWithIMEICompatibility: TODO[];
  setOldPageNumber: (page: number) => void;
  oldAllChecked: boolean;
  oldPageNumber: number;
  onSimRowCheck: (sim: TODO, selectionType?: 'single' | 'all') => void;
  goToSimDetailsView: (iccid: string) => void;
  getFilteredSimsParams: () => GetFilteredSimsParams;
  singleSimAction: (
    key: 'activate' | 'delete' | 'activateEsimbyID',
    sim: SimRecord
  ) => void;
  handleZipcodeChange: (
    simId: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  handleIMEIChange: (
    simId: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  setSortingKey: (key: SprSimListSortKey) => void;
};

type YourSimsContextState = {
  isUserAllowedToAddClient: boolean;
  simsPerPage: number;
  activeTab: SprSimListTabName;
};

type YourSimsContextActions = {};

type YourSimsContextType = YourSimsContextState &
  YourSimsContextActions &
  YourSimsContextProviderProps & {};

const YourSimsContext = React.createContext<YourSimsContextType | null>(null);

/**
 * Provider component for the YourSims to be used in the YourSims View
 *
 * @description
 * We are using this as a migratino strategy so we don't have to refactor all the logic at the same time and we can incrementally migrate to smaller functional components.
 *
 * We are using a provider so that we have a centralized place to control all the state and actions to be used in the YourSims View. This is to avoid having to pass the same props to all the components.
 *
 * @todo To replace _all_ the props with context state or local state within the child components.
 *
 * @see `YourSims/hooks.ts` -> `useYourSimsContext()` to consume the context.
 */
function YourSimsContextProvider({
  children,
  ...props
}: PropsWithChildren<YourSimsContextProviderProps>) {
  const { user, isAdmin } = useAuthContext();
  const [simsPerPage] = useState(50);

  const location = useLocation();

  const activeTab =
    TABS_PATH_NAME_MAP[location.pathname as keyof typeof TABS_PATH_NAME_MAP] ??
    SprSimListTabName.ACTIVE;

  const isUserAllowedToAddClient =
    isAdmin() ||
    user?.role === UserRole.SPR_PARTNER ||
    (user?.role === UserRole.SUB_USER &&
      checkSubUserPermissions(user, ['/add-client']));

  return (
    <YourSimsContext.Provider
      value={{
        ...props,
        isUserAllowedToAddClient,
        simsPerPage,
        activeTab,
      }}
    >
      <ModalsProvider>{children}</ModalsProvider>
    </YourSimsContext.Provider>
  );
}

export default YourSimsContext;

export { YourSimsContextProvider };
